import React, {useState, useEffect} from 'react'
import GenericTable from '../GenericTable/table'
import RadioButtons from '../GenericTable/radio_buttons'
import {LeadData} from './helper'
import SkeletonCard from '../Auth/Components/SkeletonCard'
import LeadModal from '../LeadForm/leadformModal'
import LeadEditModal from '../LeadForm/leadEditModal'
import DatePicker from '../GenericTable/CommonDatePicker'
import LeadCard from '../LeadForm/leadCard'
import {leadCardData} from '../LeadForm/helper'
import {fetchLeadsData} from './helper'
import {updateLeads} from '../../../api'
import LeadsChart from '../LeadForm/leadCharts'
import Swal from 'sweetalert2'

type SortConfig = {key: string; direction: 'asc' | 'desc' | null}
const LeadManagment = () => {
  const [columns, setColumns] = useState<string[]>([])
  const [data, setData] = useState<Record<string, any>[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [tabLabel, setTabLabel] = useState('')
  const [sortConfig, setSortConfig] = useState<SortConfig>({key: '', direction: null})
  const [loading, setLoading] = useState<boolean>(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [showCheckbox, setShowCheckbox] = useState(true)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [selectedRow, setSelectedRow] = useState<Record<string, any> | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [currentPageData, setCurrentPageData] = useState<Record<string, any>[]>([])
  const [isCardVisible, setIsCardVisible] = useState(true)
  const [selectedDays, setSelectedDays] = useState({
    startDate: '',
    endDate: '',
  })
  const itemsPerPage = 10

  const handleOpenModal = () => setIsModalOpen(true)
  const handleCloseModal = () => setIsModalOpen(false)

  // const handleTabClick = (tab: string) => {

  const columnToSortKeyMap: Record<string, string> = {
    Name: 'name',
    Email: 'email',
    Phone: 'phone',
    Status: 'status',
    'Created At': 'created_at',
    Type: 'type',
    Comment: 'comment',
    Assignee: 'assignee_id',
    Action: '', // No API sorting needed
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const sortKey = columnToSortKeyMap[sortConfig.key] || 'name' // Default to 'name' if key is not found
      const response = await fetchLeadsData(currentPage, sortKey, sortConfig.direction || 'asc')

      setColumns([
        'Name',
        'Email',
        'Phone',
        'Status',
        'Created At',
        'Type',
        'Comment',
        'Assignee',
        'Action',
      ])
      setData(response.leads)
      setTotalPages(response.totalPages)
    } catch (error) {
      console.error('Error fetching leads:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [currentPage, sortConfig])

  const mapDataToColumns = (columns: string[], data: Record<string, any>[]) => {
    return data.map((row) => {
      const mappedRow = {}
      columns.forEach((column) => {
        const key = column.replace(/\s+/g, '')
        mappedRow[column] =
          column === 'Status' && (row.Type === 'Bug' || row.Type === 'Improvement')
            ? row[column] || 'Open'
            : row[key] || ''
      })
      return mappedRow
    })
  }

  const sortData = (data: Record<string, any>[], column: string) => {
    const sortableData = [...data]
    sortableData.sort((a, b) => {
      const aValue = a[column]
      const bValue = b[column]

      const aDate = Date.parse(aValue)
      const bDate = Date.parse(bValue)
      if (!isNaN(aDate) && !isNaN(bDate)) {
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate
      }

      if (!isNaN(aValue) && !isNaN(bValue)) {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue)
      }

      return 0
    })

    return sortableData
  }

  const handleSort = (column: string) => {
    setSortConfig((prevConfig) => {
      const direction = prevConfig.key === column && prevConfig.direction === 'asc' ? 'desc' : 'asc'
      return {key: column, direction}
    })
  }

  useEffect(() => {
    if (sortConfig.key) {
      const sortedData = sortData(data, sortConfig.key)
      setData(sortedData)
    }
  }, [sortConfig])

  const handleEditClick = (row) => {
    console.log('Selected Row Data:', row)
    setSelectedRow(row)
    setIsEditModalOpen(true)
  }

  const handleNextClick = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1)
    }
  }

  const handlePreviousClick = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1)
    }
  }

  const handleStatusChange = (index: number, newStatus: string) => {
    const selectedRow = data[index]

    if (!selectedRow) {
      Swal.fire('Error', 'Selected row not found.', 'error')
      return
    }

    Swal.fire({
      title: `Are you sure you want to update status of this user ${selectedRow.Name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Call the API to update status in backend

          await updateLeads({lead_id: selectedRow.id, status: newStatus})

          // Update status in local state only if API call succeeds
          setData((prevData) => {
            const updatedData = [...prevData]
            updatedData[index] = {...updatedData[index], Status: newStatus}
            return updatedData
          })
          await fetchData()

          Swal.fire('Updated!', `Status has been changed to ${newStatus}.`, 'success')
        } catch (error) {
          Swal.fire('Error', 'Failed to update status .', 'error')
        }
      }
    })
  }

  useEffect(() => {
    const filteredData = data.filter((row) =>
      Object.values(row).join(' ').toLowerCase().includes(searchTerm.toLowerCase())
    )

    // Calculate total pages and set the current page data
    setTotalPages(Math.ceil(filteredData.length / itemsPerPage))
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    setCurrentPageData(filteredData.slice(startIndex, endIndex))
  }, [data, currentPage, searchTerm])

  return (
    <div>
      <div style={{display: 'flex', justifyContent: 'center', marginBottom: '10px'}}>
        <button
          style={{
            position: 'relative',
            padding: '5px',
            backgroundColor: 'white',
            border: '1px solid #6D9300',
            borderRadius: '10px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => setIsCardVisible((prev) => !prev)}
        >
          {isCardVisible ? (
            <i className='ki-solid ki-double-up fs-1' style={{color: '#6D9300', margin: '0'}}></i>
          ) : (
            <i className='ki-solid ki-double-down fs-1' style={{color: '#6D9300', margin: '0'}}></i>
          )}
        </button>
      </div>

      {isCardVisible && (
        <div className='d-flex mb-4' style={{width: '100%', gap: '10px'}}>
          {/* Cards Section (40%) */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)', // Two columns
              columnGap: '2px', 
              rowGap: '2px', // Keep row spacing
              width: '40%',
            }}
          >
            {/* First Row: Two Cards (50% each) */}
            {leadCardData.slice(0, 2).map((card) => (
              <div key={card.id} style={{width: '100%',marginBottom: '1px'}}>
                <LeadCard data={card} />
              </div>
            ))}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)', 
                columnGap: '2px', 
                rowGap: '2px',
                gridColumn: 'span 2', // Span across both columns
              }}
            >
              {leadCardData.slice(2).map((card) => (
                <LeadCard key={card.id} data={card} />
              ))}
            </div>
          </div>

          {/* Chart Section (60%) */}
          <div style={{width: '60%', backgroundColor: '#FFFFFF', padding: '10px'}}>
            <LeadsChart />
          </div>
        </div>
      )}

      {loading ? (
        <SkeletonCard />
      ) : (
        <>
          <GenericTable
            columns={columns}
            handleNextClick={handleNextClick}
            handlePriviousClick={handlePreviousClick}
            data={currentPageData}
            handleSort={handleSort}
            sortConfig={sortConfig}
            searchTerm={searchTerm}
            showCheckbox={showCheckbox}
            onSearch={(term) => {
              setSearchTerm(term)
              setCurrentPage(1)
            }}
            renderCell={(column, row, index) => {
              if (column === 'Status') {
                return (
                  <select
                    key={row.id}
                    value={row.Status} // Use the correct value from the row data
                    onChange={(e) => handleStatusChange(index, e.target.value)}
                    className='form-select form-select-solid'
                    style={{minWidth: '150px'}}
                    data-control='select2'
                    data-placeholder='Select an option'
                  >
                    {/* Explicitly define the dropdown options */}
                    <option value='fresh-lead'>Fresh Lead</option>
                    <option value='In Progress'>In Progress</option>
                    <option value='Reviewed'>Reviewed</option>
                    <option value='Converted'>Converted</option>
                    <option value='Rejected'>Rejected</option>
                  </select>
                )
              }

              if (column === 'Action') {
                return (
                  <>
                    <button className='btn btn-icon' onClick={() => handleEditClick(row)}>
                      <i className='ki-solid ki-user-edit fs-2x'></i>
                    </button>
                  </>
                )
              }

              return row[column]
            }}
            conditionalClass='card card-flush mb-6 mb-xl-9'
            // LeftSideElement={<DatePicker setSelectedDays={setSelectedDays} activeTab={''} />}
            RightSideElement={
              <>
                <button
                  className='btn'
                  onClick={handleOpenModal}
                  style={{
                    backgroundColor: '#6D9300',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginLeft: '2vw',
                  }}
                >
                  <i className='ki-solid ki-abstract-10' style={{color: '#fff'}}></i>
                  Add a New Lead
                </button>
                <LeadModal
                  isOpen={isModalOpen}
                  onClose={handleCloseModal}
                  onCreateLead={fetchData}
                />
              </>
            }
          />
          <ul className='pagination justify-content-center' style={{margin: '15px 0'}}>
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => {
                  if (handlePreviousClick) handlePreviousClick()
                }}
                disabled={currentPage === 1}
                aria-label='Previous'
              >
                &laquo; Previous
              </button>
            </li>

            <li className='page-item active'>
              <div className='d-flex align-items-center'>
                <button
                  className='page-link'
                  style={{backgroundColor: '#6D9300', color: '#fff', pointerEvents: 'none'}}
                >
                  {currentPage}
                </button>
                <span className='mx-2'>of</span>
                <button
                  className='page-link'
                  style={{backgroundColor: '#6D9300', color: '#fff', pointerEvents: 'none'}}
                >
                  {totalPages}
                </button>
              </div>
            </li>

            <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => {
                  if (handleNextClick) handleNextClick()
                }}
                disabled={currentPage >= totalPages}
                aria-label='Next'
              >
                Next &raquo;
              </button>
            </li>
          </ul>
        </>
      )}
      <LeadEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        selectedRow={selectedRow}
        onUpdate={fetchData}
      />
    </div>
  )
}

export default LeadManagment
